import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  AUDIENCE_TYPE_LABELS,
  OBJECTIVE_LABELS,
  SECONDARY_GOAL_LABELS,
} from './mediaPlanLabels';
import { ExperimentStatus } from '../types';

import type { MediaPlanDetail } from '../types';

import { useGetExperimentQuery } from '@/api/endpoints/mediaPlan';
import { useGetUserInfoQuery } from '@/api/endpoints/users';
import { Info } from '@/shared/components/icons';
import FileSpreadsheet from '@/shared/components/icons/lucide/FileSpreadsheet';
import FlagTriangleRight from '@/shared/components/icons/lucide/FlagTriangleRight';
import FlaskConical from '@/shared/components/icons/lucide/FlaskConical';
import Monitor from '@/shared/components/icons/lucide/Monitor';
import Tag from '@/shared/components/icons/lucide/Tag';
import Target from '@/shared/components/icons/lucide/Target';
import Users from '@/shared/components/icons/lucide/Users';
import { Tooltip } from '@/shared/components/tooltip';

interface DetailItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  truncate?: boolean;
}

const DetailItem = ({
  icon,
  label,
  value,
  truncate = false,
}: DetailItemProps) => {
  const displayValue = value || '—';
  const valueNode = (
    <Typography
      variant='body1'
      sx={
        truncate
          ? {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }
          : undefined
      }
    >
      {displayValue}
    </Typography>
  );

  return (
    <Stack direction='row' gap={1.5} alignItems='flex-start'>
      <Box sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant='body2' color='text.secondary' mb={0.25}>
          {label}
        </Typography>
        {truncate && value ? (
          <Tooltip title={value}>{valueNode}</Tooltip>
        ) : (
          valueNode
        )}
      </Box>
    </Stack>
  );
};

interface MediaPlanSummaryProps {
  plan: MediaPlanDetail;
}

const EXPERIMENT_STATUS_LABELS: Record<ExperimentStatus, string> = {
  [ExperimentStatus.Active]: 'Active',
  [ExperimentStatus.Pending]: 'Pending Approval',
  [ExperimentStatus.Rejected]: 'Request Rejected',
};

const MediaPlanSummary = ({ plan }: MediaPlanSummaryProps) => {
  const { data: userInfo } = useGetUserInfoQuery(null);
  const { data: experiment, isLoading: isExperimentLoading } =
    useGetExperimentQuery({ id: plan.id });
  const isMeta = plan.platform === 'META';
  const brand = isMeta
    ? (userInfo?.selectedAccount?.name ?? plan.accountName ?? '')
    : (userInfo?.selectedAdvertiser?.name ?? plan.advertiserName ?? '');

  const audience = plan.audienceLibrary
    ? plan.audienceLibrary.name
    : (AUDIENCE_TYPE_LABELS[plan.audienceType] ?? plan.audienceType);

  const secondaryGoal = plan.secondaryGoal
    ? (SECONDARY_GOAL_LABELS[plan.secondaryGoal] ?? plan.secondaryGoal)
    : '';

  return (
    <Stack gap={1.5}>
      <Typography variant='h6' fontWeight={600}>
        Overview
      </Typography>

      <Box
        sx={(theme) => ({
          bgcolor: theme.palette.primary[50],
          borderRadius: 2,
          p: 2.5,
        })}
      >
        <Stack gap={2.5}>
          <Stack direction='row' gap={1.5} alignItems='flex-start'>
            <Box sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}>
              <FileSpreadsheet sx={{ strokeWidth: 1 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction='row' gap={0.5} alignItems='center' mb={0.25}>
                <Typography variant='body2' color='text.secondary'>
                  Media Plan Name
                </Typography>
                {plan.description ? (
                  <Tooltip title={plan.description}>
                    <Box
                      component='span'
                      sx={{
                        color: 'primary.main',
                        display: 'inline-flex',
                        cursor: 'help',
                      }}
                    >
                      <Info sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography variant='body1'>{plan.name || '—'}</Typography>
            </Box>
          </Stack>

          <Stack
            direction='row'
            justifyContent='space-between'
            gap={4}
            flexWrap='wrap'
            useFlexGap
          >
            <DetailItem
              icon={<Monitor sx={{ strokeWidth: 1 }} />}
              label='Platform'
              value={plan.platform}
            />
            <DetailItem
              icon={<Tag sx={{ strokeWidth: 1 }} />}
              label={isMeta ? 'Account' : 'Advertiser'}
              value={brand}
            />
            <DetailItem
              icon={<Target sx={{ strokeWidth: 1 }} />}
              label='Brand Objective'
              value={OBJECTIVE_LABELS[plan.dataMode] ?? plan.dataMode}
            />
            <DetailItem
              icon={<FlagTriangleRight sx={{ strokeWidth: 1 }} />}
              label='Secondary Goal'
              value={secondaryGoal}
            />
            <DetailItem
              icon={<Users sx={{ strokeWidth: 1 }} />}
              label='Audience'
              value={audience}
            />
            <DetailItem
              icon={<FlaskConical sx={{ strokeWidth: 1 }} />}
              label='Experiment'
              value={
                isExperimentLoading
                  ? '-'
                  : experiment?.status
                    ? EXPERIMENT_STATUS_LABELS[experiment.status]
                    : 'Inactive'
              }
            />
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

export default MediaPlanSummary;
